import { saveState } from './stateManagerHelper';
/**
 * A single multi-select drag gesture can involve MULTIPLE independent leader/follower systems
 * contributing entries at once: the central useMultiSelectFollowerEngine (useMultiSelectDrag.ts)
 * for plain manipulative types, plus each SELF_MANAGED_TYPES widget's own bespoke same-type
 * batching (ColoredTiles, LinearPiece, CoinsAndCounters, FractionTiles, LinkingCubes), plus
 * several leader widgets that dispatch multiSelectMoveStart/Move/End directly and do their own
 * local batching (NMP, NumberFrames, Counter, PatternShapes, NumberLine, Table, ManipulativeGroup,
 * Shades). Previously each of these called `saveState()` independently — since stateManager.save()
 * never merges (every call is a discrete history.push), ONE user drag produced 2+ separate undo
 * steps, so a single Ctrl+Z only reverted part of what moved. This module is the single point
 * every contributor now calls into instead, so exactly one saveState() fires per gesture.
 */
var active = false;
var gestureSourceId = null;
var pendingEntries = [];
var flushTimeoutId = null;
function dedupeEntries(entries) {
    // Defensive: keep the LAST entry for any (type, id, propertyName) triple. Should not normally
    // trigger — SELF_MANAGED_TYPES/CONTAINMENT_SKIP_RULES already prevent double-capture of the
    // same field by two contributors — but guards against any future classification edge case.
    var byKey = new Map();
    for (var _i = 0, entries_1 = entries; _i < entries_1.length; _i++) {
        var entry = entries_1[_i];
        var key = "".concat(entry.type, "::").concat(entry.id, "::").concat(entry.propertyName);
        byKey.set(key, entry);
    }
    return Array.from(byKey.values());
}
function flush() {
    if (flushTimeoutId !== null) {
        clearTimeout(flushTimeoutId);
        flushTimeoutId = null;
    }
    if (pendingEntries.length > 0) {
        var deduped = dedupeEntries(pendingEntries);
        saveState(deduped);
    }
    active = false;
    gestureSourceId = null;
    pendingEntries = [];
}
/**
 * Resets the collector for a new gesture. Called once, from the central engine's onStart
 * (useMultiSelectFollowerEngine mounts exactly once app-wide, so it's the one reliable reset
 * point regardless of which widget ends up leading).
 *
 * If a PREVIOUS gesture's entries are still pending (two multi-select drags started faster than
 * the deferred flush), synchronously flushes them right now before resetting, so no gesture's
 * undo entry is ever silently discarded by the reset.
 */
export function beginMultiSelectSaveBatch(sourceId) {
    if (pendingEntries.length > 0) {
        flush();
    }
    else if (flushTimeoutId !== null) {
        clearTimeout(flushTimeoutId);
        flushTimeoutId = null;
    }
    active = true;
    gestureSourceId = sourceId;
    pendingEntries = [];
}
/**
 * Contributor entry point — call this INSTEAD of saveState(entries) for a multi-select-drag
 * batch. Returns false (and does nothing) if the coordinator isn't active for this exact
 * gesture (e.g. multiSelectMoveStart never fired — effectively a solo drag despite the widget's
 * own multi-select branch running). Callers MUST fall back to `saveState(entries)` themselves
 * when this returns false, so no entry is ever lost regardless of a given widget's own
 * multi-select detection being imperfect:
 *
 *   if (!registerMultiSelectSaveEntries(id, entries)) saveState(entries);
 */
export function registerMultiSelectSaveEntries(sourceId, entries) {
    if (!active || sourceId !== gestureSourceId) {
        return false;
    }
    if (entries.length > 0)
        pendingEntries.push.apply(pendingEntries, entries);
    if (flushTimeoutId === null) {
        flushTimeoutId = setTimeout(flush, 0);
    }
    return true;
}
/**
 * Force-clears the collector WITHOUT saving pending entries — for a genuinely aborted/cancelled
 * gesture where nothing should be recorded. Wired into the central engine's hardReset() fallback.
 */
export function cancelMultiSelectSaveBatch() {
    if (flushTimeoutId !== null) {
        clearTimeout(flushTimeoutId);
        flushTimeoutId = null;
    }
    active = false;
    gestureSourceId = null;
    pendingEntries = [];
}
